/**
 * StatusUpdateSection — composer + feed for Oppdatering on a subject.
 * Drop-in for project / customer / activity / work order:
 *   <StatusUpdateSection subjectType="project" subjectId={project.id} customer={project.customer} />
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import {
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { StatusUpdateComposer } from "./StatusUpdateComposer";
import { StatusUpdateFeed } from "./StatusUpdateFeed";
import { StatusUpdateUnavailableNotice } from "./StatusUpdateUnavailableNotice";
import {
  fetchStatusUpdates,
  isStatusUpdateModuleUnavailable,
} from "../../../api/statusUpdates";

export function StatusUpdateSection({
  subjectType,
  subjectId,
  customer,
  projectId = null,
  project = null,
  title,
  compact = false,
  /** Hide composer when the user may read the feed but not post (legacy notes stay read-only). */
  canCompose = true,
}) {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [queryInput, setQueryInput] = useState("");
  const [query, setQuery] = useState("");
  const requestIdRef = useRef(0);

  const debouncedSetQuery = useMemo(
    () =>
      debounce((value) => {
        setQuery(value);
      }, 300),
    []
  );

  useEffect(() => () => debouncedSetQuery.cancel(), [debouncedSetQuery]);

  // On subject change, drop any cached posts from a previous subject immediately.
  useEffect(() => {
    setPosts([]);
    setQueryInput("");
    setQuery("");
    debouncedSetQuery.cancel();
  }, [subjectType, subjectId, debouncedSetQuery]);

  const load = useCallback(async () => {
    if (!subjectType || !subjectId) return;
    const reqId = ++requestIdRef.current;
    setLoading(true);
    setLoadError(false);
    try {
      const res = await fetchStatusUpdates({
        subjectType,
        subjectId,
        q: query,
      });
      if (reqId !== requestIdRef.current) return;
      if (isStatusUpdateModuleUnavailable(res)) {
        setUnavailable(true);
        // Keep any previously loaded posts (e.g. transient publish 404 after a good list).
        return;
      }
      if (res?.__error) {
        setUnavailable(false);
        setLoadError(true);
        setPosts([]);
        return;
      }
      setUnavailable(false);
      setPosts(Array.isArray(res?.data) ? res.data : []);
    } catch {
      // Network blips should not hard-fail the host page.
      if (reqId === requestIdRef.current) {
        setLoadError(true);
        setPosts([]);
      }
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, [subjectType, subjectId, query]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreated = (post) => {
    setUnavailable(false);
    if (query) {
      setQueryInput("");
      setQuery("");
      return;
    }
    if (post?.id) setPosts((prev) => [post, ...prev.filter((p) => p.id !== post.id)]);
  };

  const handleDeleted = (id) => {
    setPosts((prev) => prev.filter((p) => p.id !== id));
  };

  const handleUpdated = (updated) => {
    if (!updated?.id) return;
    setPosts((prev) => {
      const merged = prev.map((p) =>
        p.id === updated.id
          ? { ...p, ...updated, appeared_via: updated.appeared_via || p.appeared_via }
          : p
      );
      if (
        subjectType !== "project" &&
        subjectType !== "customer" &&
        subjectType !== "job_opening"
      ) {
        return merged;
      }
      return merged.filter((p) => {
        if (p.id !== updated.id) return true;
        const key = String(p.subject_key || "").toLowerCase();
        if (key === subjectType && Number(p.subject_id) === Number(subjectId)) return true;
        return (p.tags || []).some(
          (tg) => tg.module === subjectType && String(tg.module_param) === String(subjectId)
        );
      });
    });
  };

  const handleUnavailable = () => {
    // Publish/module probe failure: hide composer, keep any feed already loaded.
    setUnavailable(true);
    setLoadError(false);
  };

  const handleSearchChange = (value) => {
    setQueryInput(value);
    debouncedSetQuery(value);
  };

  const headingClass = compact
    ? "text-sm font-semibold text-slate-900"
    : "text-base font-semibold text-slate-900";

  return (
    <section id="oppdatering" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <ChatBubbleLeftRightIcon className="h-5 w-5 text-[#1463C7]" />
        <h3 className={headingClass}>{title || t("Oppdatering")}</h3>
        <div className="relative ml-auto flex h-9 min-w-0 flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 focus-within:border-slate-400 focus-within:ring-2 focus-within:ring-slate-200 sm:max-w-xs">
          <MagnifyingGlassIcon className="h-4 w-4 flex-shrink-0 text-slate-400" />
          <input
            type="search"
            value={queryInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={t("status_update.search_placeholder")}
            className="min-w-0 flex-1 border-none bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
          />
          {queryInput ? (
            <button
              type="button"
              onClick={() => handleSearchChange("")}
              className="flex-shrink-0 text-slate-400 hover:text-slate-600"
              aria-label={t("Clear")}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>

      {unavailable ? (
        <StatusUpdateUnavailableNotice compact={compact} onRetry={load} retrying={loading} />
      ) : loadError ? (
        <div
          role="alert"
          className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900"
        >
          <p className="font-semibold">{t("status_update.load_error")}</p>
          <button
            type="button"
            onClick={load}
            className="mt-2 text-sm font-semibold text-[#0B3A78] underline-offset-2 hover:underline"
          >
            {t("status_update.retry")}
          </button>
        </div>
      ) : canCompose ? (
        <StatusUpdateComposer
          subjectType={subjectType}
          subjectId={subjectId}
          customer={customer}
          projectId={projectId}
          project={project}
          onCreated={handleCreated}
          onModuleUnavailable={handleUnavailable}
        />
      ) : null}

      {/* Keep a successfully loaded feed visible even when publish latches unavailable. */}
      {(!unavailable || posts.length > 0) && !loadError ? (
        <StatusUpdateFeed
          posts={posts}
          loading={loading && posts.length === 0}
          customer={customer}
          emptyMessage={
            query
              ? t("status_update.search_empty")
              : t("Ingen oppdateringer ennå. Skriv den første!")
          }
          onDeleted={handleDeleted}
          onUpdated={handleUpdated}
        />
      ) : null}
    </section>
  );
}

export default StatusUpdateSection;
